// @flow
/* eslint-disable class-methods-use-this, no-unused-vars, no-console */
import IntlMessageFormat from 'intl-messageformat'
import memoizeIntlConstructor from 'intl-format-cache'
import DEFAULT_FORMATS from './DefaultFormats'
import { findLanguage, type LanguageDescriptor } from './languages'

// Types
import type { Message } from '../create'

type Props = {
  formats?: Object,
  locale: string,
}

/**
 * Dummy messages manager which just formats default message text.
 * Used as a fallback in {@link formatMessage} when actual manager
 * is not provided by {@link IntlProvider}
 */
class DefaultMessagesManager {
  formats = DEFAULT_FORMATS

  locale: string

  localeDescriptor: LanguageDescriptor

  formatterCache = memoizeIntlConstructor(IntlMessageFormat)

  constructor({ formats = DEFAULT_FORMATS, locale = 'en-us' }: Props = {}) {
    this.formats = formats
    this.locale = locale
    this.localeDescriptor = findLanguage(locale)
  }

  changeLocale(newLocale: string): boolean {
    if (newLocale === this.locale) return false

    this.locale = newLocale
    this.localeDescriptor = findLanguage(newLocale)
    this.formatterCache = memoizeIntlConstructor(IntlMessageFormat)
    return true
  }

  appendMessages(messages: Object) {
    // do nothing
  }

  getActiveLocale() {
    return this.locale
  }

  getActiveLocaleDescriptor() {
    return this.localeDescriptor
  }

  /**
   * Returns true when all locale necessary stuff (moment locale, react-intl
   * locale) loaded and ready to be used
   * @return {Boolean} [description]
   */
  isLocaleReady() {
    return true
  }

  /**
   * Calls given callback when all locale necessary stuff (moment locale, react-intl
   * locale) loaded and ready to be used
   * @param  {Function} callback
   */
  whenLocaleReady(callback: Function) {
    callback()
  }

  /**
   * Returns true when given message (actually domain of the message)
   * loaded and can be used from messages object.
   * @param  {Object}  message
   * @return {Boolean}
   */
  isMessageReady(message: Message): boolean {
    return true
  }

  /**
   * Load domain of given message and execute given handler
   * (if provided) when the domain will loaded.
   * Internally it adds the loading request to a queue of
   * requested domains and try to get all queued domains
   * by actual queue processing function, which is debounced
   * a little bith to load domains with batches.
   * @param  {Message} message
   * @param  {Function} handler
   */
  loadMessage(message: Message, handler?: Function) {
    // do nothing
  }

  /**
   * Returns a formatter for given message string. It uses
   * internal cache for formatter instances to improve performance.
   * @param  {string} message
   * @return {Formatter}
   */
  getFormatter(message: string, customFormats?: Object) {
    const finalFormats = customFormats
      ? { ...this.formats, ...customFormats }
      : this.formats

    return this.formatterCache(
      message,
      this.localeDescriptor.intl,
      finalFormats
    )
  }

  /**
   * Returns translated message for given message descriptor.
   * It fallback to defaultMessage or id of the descriptor if
   * no translation for given message found.
   * @param  {Message} descr
   * @return {string}
   */
  getTranslation(descr: Message) {
    return descr.defaultMessage || descr.id
  }

  /**
   * Translated string could be broken by the translators. Or a developer
   * could forget to give all required variables to format the message.
   * This method invoked when formatter failed to format the message and
   * it invoke provided error handler to further escalation (or logging)
   * @param  {any} e
   */
  handleFormatError(e: any) {
    console.error(e)
  }
}

export default DefaultMessagesManager
